export type InventoryPageSignalKind = 'cannibalization' | 'internal_link' | 'no_cluster' | 'unclustered';

export interface InventoryPageSignal {
  kind: InventoryPageSignalKind;
  label: string;
  title: string;
}

export type ActionPlanRowStatus = 'open' | 'done' | 'dismissed';

export interface CannibalizationSignalRow {
  status: ActionPlanRowStatus;
  loserUrlId: string;
  urlAId: string;
  urlBId: string;
  mergeGrade?: 'merge' | 'review';
  sharedKeywordsCount?: number;
}

export interface InternalLinkSignalRow {
  status: ActionPlanRowStatus;
  sourceUrlId: string;
  targetUrlId: string;
}

export interface UnclusteredSignalRow {
  status: ActionPlanRowStatus;
  urlId: string;
}

export interface PageSignalSourceRow {
  urlId: string;
  clusters?: string[];
}

const SIGNAL_ORDER: InventoryPageSignalKind[] = [
  'cannibalization',
  'internal_link',
  'unclustered',
  'no_cluster'
];

export function buildPageSignalsForUrl(
  urlId: string,
  clusters: string[] | undefined,
  cannibalRows: CannibalizationSignalRow[],
  internalLinkRows: InternalLinkSignalRow[],
  unclusteredRows: UnclusteredSignalRow[]
): InventoryPageSignal[] {
  const normalizedId = (urlId || '').trim();
  if (!normalizedId) return [];

  const signals: InventoryPageSignal[] = [];

  const openCannibal = cannibalRows.some((row) => {
    if (row.status !== 'open') return false;
    if (row.loserUrlId !== normalizedId && row.urlAId !== normalizedId && row.urlBId !== normalizedId) {
      return false;
    }
    if (row.mergeGrade === 'review') return false;
    if (row.mergeGrade === 'merge') return true;
    return (row.sharedKeywordsCount ?? 0) >= 2;
  });
  if (openCannibal) {
    signals.push({
      kind: 'cannibalization',
      label: 'Cannibal',
      title: 'Open merge-grade cannibalization candidate for this URL'
    });
  }

  const openInternalLink = internalLinkRows.some(
    (row) => row.status === 'open' && row.targetUrlId === normalizedId
  );
  if (openInternalLink) {
    signals.push({
      kind: 'internal_link',
      label: 'Link',
      title: 'Open internal-link action-plan suggestion involving this URL'
    });
  }

  const openUnclustered = unclusteredRows.some((row) => row.status === 'open' && row.urlId === normalizedId);
  if (openUnclustered) {
    signals.push({
      kind: 'unclustered',
      label: 'Unclustered',
      title: 'Open unclustered-page action-plan suggestion for this URL'
    });
  }

  const clusterLabels = Array.isArray(clusters) ? clusters.filter((c) => typeof c === 'string' && c.trim()) : [];
  if (clusterLabels.length === 0) {
    signals.push({
      kind: 'no_cluster',
      label: 'No cluster',
      title: 'This URL is not assigned to any workspace plan cluster'
    });
  }

  return signals.sort((a, b) => SIGNAL_ORDER.indexOf(a.kind) - SIGNAL_ORDER.indexOf(b.kind));
}

export function buildPageSignalsIndex(
  pages: PageSignalSourceRow[],
  cannibalRows: CannibalizationSignalRow[],
  internalLinkRows: InternalLinkSignalRow[],
  unclusteredRows: UnclusteredSignalRow[]
): Map<string, InventoryPageSignal[]> {
  const index = new Map<string, InventoryPageSignal[]>();
  for (const page of pages) {
    const urlId = (page.urlId || '').trim();
    if (!urlId) continue;
    index.set(
      urlId,
      buildPageSignalsForUrl(urlId, page.clusters, cannibalRows, internalLinkRows, unclusteredRows)
    );
  }
  return index;
}
